import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import SDRLayout from "../../components/sdr/SDRLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, Trophy, Plus, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const SDRDashboard = () => {
  const { API, token, user } = useAuth();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", date: new Date().toISOString().split("T")[0], notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = () => {
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/sdr/dashboard`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/sdr/leaderboard`, { headers: h }).then(r => r.ok ? r.json() : null),
    ]).then(([d, lb]) => {
      setData(d);
      setLeaderboard(lb || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [API, token]);

  const handleLogAppt = async () => {
    if (!form.client_name || !form.date) { toast.error("Name and date required"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/sdr/appointments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) { toast.success("Appointment logged"); setLogOpen(false); setForm({ client_name: "", client_email: "", date: new Date().toISOString().split("T")[0], notes: "" }); fetchAll(); }
      else { const d = await r.json(); toast.error(d.detail || "Failed"); }
    } catch { toast.error("Network error"); }
    setSubmitting(false);
  };

  if (loading) return <SDRLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500" /></div></SDRLayout>;

  const myCode = user?.agent_code;
  const stats = [
    { label: "Appointments Set", value: data?.total_appointments || 0, icon: CalendarDays, gradient: "from-sky-500 to-blue-600" },
    { label: "Hours Logged", value: `${(data?.total_hours || 0).toFixed(1)}h`, icon: Clock, gradient: "from-violet-500 to-purple-600" },
  ];

  return (
    <SDRLayout>
      <div className="space-y-6" data-testid="sdr-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]">Welcome back, {user?.name?.split(" ")[0]}</h1>
            <p className="text-sm text-gray-500 mt-0.5">Rep Code: <span className="font-mono font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">{data?.agent_code}</span></p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setLogOpen(true)} className="bg-sky-600 hover:bg-sky-700 gap-2 text-sm h-9" data-testid="log-appt-btn"><Plus className="w-3.5 h-3.5" /> Log Appointment</Button>
            <Link to="/sdr/calendar"><Button variant="outline" className="gap-2 text-sm h-9"><CalendarDays className="w-3.5 h-3.5" /> Book</Button></Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          {stats.map((s, i) => (
            <Card key={s.key || s.name || s.title || i} className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-5">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shrink-0`}>
                    <s.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">{s.label}</p>
                    <p className="text-3xl font-bold text-[#1A2636] mt-0.5">{s.value}</p>
                  </div>
                </div>
                <div className={`h-1 bg-gradient-to-r ${s.gradient}`} />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Leaderboard */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#1A2636] text-sm">Appointments Leaderboard</h3>
                </div>
                <Link to="/sdr/leaderboard" className="text-xs text-sky-600 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((e, i) => {
                  const isMe = e.agent_code === myCode;
                  return (
                    <div key={e.agent_code} className={`flex items-center justify-between py-2 px-3 rounded-lg ${isMe ? "bg-sky-50 ring-1 ring-sky-200" : i < 3 ? "bg-gray-50" : ""}`}>
                      <div className="flex items-center gap-2.5">
                        <span className={`text-sm font-bold w-5 ${i === 0 ? "text-yellow-500" : i === 1 ? "text-gray-400" : i === 2 ? "text-amber-700" : "text-gray-400"}`}>{i + 1}</span>
                        <span className="text-sm font-medium text-[#1A2636]">
                          {e.name}
                          {isMe && <Badge className="ml-1.5 bg-sky-100 text-sky-700 text-[9px] px-1 py-0">You</Badge>}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-[#1A2636]">{e.appointments}</span>
                    </div>
                  );
                })}
                {!leaderboard.length && <p className="text-xs text-gray-400 text-center py-3">No data yet</p>}
              </div>
            </CardContent>
          </Card>

          {/* Recent appointments */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-semibold text-[#1A2636] text-sm">Recent Appointments</h3>
              </div>
              {data?.recent_appointments?.length ? (
                <div className="space-y-2.5">
                  {data.recent_appointments.map((a) => (
                    <div key={a.appt_id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-[#1A2636]">{a.client_name}</p>
                        {a.client_email && <p className="text-[11px] text-gray-400">{a.client_email}</p>}
                      </div>
                      <span className="text-xs text-gray-500">{new Date(a.date + "T12:00:00").toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <CalendarDays className="w-7 h-7 mx-auto mb-2 opacity-40" />No appointments yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Log appointment dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log Appointment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Client Name *</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Contact name" data-testid="appt-client" /></div>
            <div><Label className="text-xs">Client Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="email@company.com" data-testid="appt-email" /></div>
            <div><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="appt-date" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Brief notes" data-testid="appt-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
            <Button onClick={handleLogAppt} disabled={submitting} className="bg-sky-600 hover:bg-sky-700" data-testid="appt-submit">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log Appointment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SDRLayout>
  );
};

export default SDRDashboard;
